// ./src/utils/api/YearlyReportService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { CurrencyAmount, ConsumptionData } from "@/types";

// ============================================
// GraphQL Query
// ============================================

const YEARLY_REPORT_QUERY = gql`
  query ReportYearly($filter: YearlyReportFilter) {
    reportYearly(filter: $filter) {
      code
      data {
        year

        # Annual Totals (Home Currency)
        totalRefuelsCostHc
        totalExpensesCostHc
        totalCostHc
        totalRefuelsCountHc
        totalExpensesCountHc

        # Annual Totals (Foreign Currencies)
        foreignRefuels {
          currency
          amount
          recordsCount
        }
        foreignExpenses {
          currency
          amount
          recordsCount
        }
        foreignCurrencyTotals {
          currency
          amount
          recordsCount
        }
        totalForeignRecordsCount

        # Annual Fuel & Mileage Metrics
        totalFuelPurchased
        totalMileage
        totalRefuelsCount
        totalExpensesCount

        # Annual Consumption Metrics (NEW)
        consumption {
          byFuelType {
            fuelType
            consumption
            consumptionUnit
            fuelUsed
            fuelUnit
            distance
            distanceUnit
            confidence
            confidenceReasons
            vehiclesCount
            refuelsCount
            dataPointsCount
          }
          totalDistance
          distanceUnit
          totalVehiclesCount
        }

        # Monthly Breakdown
        months {
          month

          # Home Currency
          refuelsCostHc
          expensesCostHc
          totalCostHc
          refuelsCountHc
          expensesCountHc

          # Foreign Currencies
          foreignRefuels {
            currency
            amount
            recordsCount
          }
          foreignExpenses {
            currency
            amount
            recordsCount
          }
          foreignCurrencyTotals {
            currency
            amount
            recordsCount
          }
          totalForeignRecordsCount

          # Fuel & Mileage Metrics
          fuelPurchased
          mileage
          refuelsCount
          expensesCount

          # Monthly Consumption Metrics (NEW)
          consumption {
            byFuelType {
              fuelType
              consumption
              consumptionUnit
              fuelUsed
              fuelUnit
              distance
              distanceUnit
              confidence
              confidenceReasons
              vehiclesCount
              refuelsCount
              dataPointsCount
            }
            totalDistance
            distanceUnit
            totalVehiclesCount
          }
        }

        # User Preferences
        distanceUnit
        volumeUnit
        consumptionUnit
        homeCurrency
        vehiclesCount
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// ============================================
// Types
// ============================================

/**
 * Filter input for yearly report
 */
export interface YearlyReportFilter {
  /** Filter by car IDs (optional - all cars if not specified) */
  carId?: string[] | null;
  /** Filter by tag IDs */
  tagId?: string[] | null;
  /** Year to report on (defaults to current year if not specified) */
  year?: number | null;
}

/**
 * Monthly breakdown data within the yearly report
 */
export interface MonthlyBreakdown {
  /** Month number (1-12) */
  month?: number;

  // Home Currency Totals
  /** Refuels cost in home currency */
  refuelsCostHc?: number;
  /** Expenses cost in home currency */
  expensesCostHc?: number;
  /** Total cost in home currency */
  totalCostHc?: number;
  /** Number of refuel records in home currency */
  refuelsCountHc?: number;
  /** Number of expense records in home currency */
  expensesCountHc?: number;

  // Foreign Currency Totals
  /** Foreign currency refuels breakdown */
  foreignRefuels?: CurrencyAmount[];
  /** Foreign currency expenses breakdown */
  foreignExpenses?: CurrencyAmount[];
  /** Combined foreign currency totals */
  foreignCurrencyTotals?: CurrencyAmount[];
  /** Total records in foreign currencies */
  totalForeignRecordsCount?: number;

  // Fuel & Mileage Metrics (all currencies)
  /** Total fuel volume purchased (in user's volume unit) */
  fuelPurchased?: number;
  /** Total mileage tracked (in user's distance unit) */
  mileage?: number;
  /** Total number of refuel events (all currencies) */
  refuelsCount?: number;
  /** Total number of expense events (all currencies) */
  expensesCount?: number;

  // Consumption Metrics (NEW)
  /**
   * Monthly consumption data grouped by fuel type.
   * May be null if no consumption data available for this month.
   */
  consumption?: ConsumptionData;
}

/**
 * Complete yearly report data
 */
export interface YearlyReport {
  /** Report year */
  year?: number;

  // Annual Totals (Home Currency)
  /** Total refuels cost in home currency for the year */
  totalRefuelsCostHc?: number;
  /** Total expenses cost in home currency for the year */
  totalExpensesCostHc?: number;
  /** Total cost in home currency for the year */
  totalCostHc?: number;
  /** Total refuel records in home currency */
  totalRefuelsCountHc?: number;
  /** Total expense records in home currency */
  totalExpensesCountHc?: number;

  // Annual Foreign Currency Totals
  /** Foreign currency refuels breakdown for the year */
  foreignRefuels?: CurrencyAmount[];
  /** Foreign currency expenses breakdown for the year */
  foreignExpenses?: CurrencyAmount[];
  /** Combined foreign currency totals for the year */
  foreignCurrencyTotals?: CurrencyAmount[];
  /** Total records in foreign currencies for the year */
  totalForeignRecordsCount?: number;

  // Annual Fuel & Mileage Metrics (all currencies)
  /** Total fuel volume purchased for the year */
  totalFuelPurchased?: number;
  /** Total mileage tracked for the year */
  totalMileage?: number;
  /** Total number of refuel events for the year (all currencies) */
  totalRefuelsCount?: number;
  /** Total number of expense events for the year (all currencies) */
  totalExpensesCount?: number;

  // Annual Consumption Metrics (NEW)
  /**
   * Annual consumption data grouped by fuel type.
   * Provides yearly average consumption with confidence levels.
   */
  consumption?: ConsumptionData;

  // Monthly Breakdown
  /** Array of 12 monthly breakdowns */
  months?: MonthlyBreakdown[];

  // User Preferences (for display formatting)
  /** User's distance unit (km/mi) */
  distanceUnit?: string;
  /** User's volume unit (L/gal) */
  volumeUnit?: string;
  /** User's consumption unit (L/100km, mpg, etc.) */
  consumptionUnit?: string;
  /** User's home currency code */
  homeCurrency?: string;
  /** Number of vehicles in the report */
  vehiclesCount?: number;
}

// ============================================
// Service Class
// ============================================

/**
 * YearlyReportService handles API calls for the yearly report.
 *
 * This report provides a month-by-month breakdown of vehicle expenses including:
 * - Monthly costs broken down by refuels and other expenses
 * - Mileage and fuel volume tracking per month
 * - Foreign currency tracking at both monthly and annual levels
 * - Annual totals and aggregations
 * - Multi-fuel-type consumption data with confidence levels (annual and monthly)
 */
export class YearlyReportService {
  /**
   * Get yearly report with optional filters.
   *
   * @param filter - Optional filters (carId, tagId, year)
   * @returns OpResult containing YearlyReport data
   *
   * @example
   * // Get report for current year, all cars
   * const result = await YearlyReportService.get();
   *
   * @example
   * // Get report for specific year and car
   * const result = await YearlyReportService.get({
   *   carId: ['car-uuid-123'],
   *   year: 2025
   * });
   *
   * @example
   * // Access annual consumption data by fuel type
   * const result = await YearlyReportService.get({ year: 2025 });
   * const report = result.data?.[0];
   * report?.consumption?.byFuelType?.forEach(ft => {
   *   console.log(`${ft.fuelType}: ${ft.consumption} ${ft.consumptionUnit}`);
   * });
   *
   * @example
   * // Access monthly consumption data
   * const result = await YearlyReportService.get({ year: 2025 });
   * const report = result.data?.[0];
   * report?.months?.forEach(month => {
   *   const primaryFuel = month.consumption?.byFuelType?.[0];
   *   if (primaryFuel) {
   *     console.log(`Month ${month.month}: ${primaryFuel.consumption} ${primaryFuel.consumptionUnit}`);
   *   }
   * });
   */
  static async get(filter?: YearlyReportFilter): Promise<OpResult> {
    const result = await ApiService.executeQuery(YEARLY_REPORT_QUERY, {
      filter: filter || {},
    });
    return result;
  }
}